import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import { getBookById } from "@/lib/supabase";
import { getUserLoanStats } from "@/lib/borrowing";
import { useAuth } from "@/hooks/useAuth";
import { TISACard } from "@/components/tisa/TISACard";
import { TISAButton } from "@/components/tisa/TISAButton";
import { BorrowBookModal } from "./BorrowBookModal";
import { IB_PYP_THEMES } from "@/types/database";
import {
  ArrowLeft,
  BookOpen,
  Users,
  Clock,
  Star,
  Tag,
  MapPin,
  Coins,
} from "lucide-react";
import { cn } from "@/lib/utils";

export function BookDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showBorrowModal, setShowBorrowModal] = useState(false);

  const {
    data: book,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["book", id],
    queryFn: () => getBookById(id!),
    enabled: !!id,
  });

  // Get user's loan statistics
  const { data: loanStats } = useQuery({
    queryKey: ["user-loan-stats", user?.id],
    queryFn: () => getUserLoanStats(user!.id),
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-6"></div>
          <TISACard className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="h-80 bg-muted rounded-lg"></div>
              <div className="md:col-span-2 space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-6 bg-muted rounded w-1/2"></div>
                <div className="h-20 bg-muted rounded"></div>
              </div>
            </div>
          </TISACard>
        </div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <TISAButton
          onClick={() => navigate(-1)}
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          Go Back
        </TISAButton>
        <TISACard variant="outline" className="text-center p-12">
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-child-xl font-bold mb-2">Book Not Found</h2>
          <p className="text-muted-foreground mb-6">
            We couldn't find the book you're looking for. It might have been
            removed or doesn't exist.
          </p>
          <TISAButton
            onClick={() => navigate("/student/books")}
            variant="theme-blue"
          >
            Browse All Books
          </TISAButton>
        </TISACard>
      </div>
    );
  }

  const theme = IB_PYP_THEMES[book.theme_color];
  // Don't fallback to empty - if copies data is missing, show as data issue
  const availableCopies =
    book.copies?.filter((copy) => copy.status === "available") ?? [];
  const totalCopies = book.copies?.length ?? 0;

  // Only allow borrow if loanStats is loaded and canBorrowMore is true
  const canBorrow =
    user?.role === "student" &&
    availableCopies.length > 0 &&
    !!loanStats &&
    loanStats.canBorrowMore === true;

  const handleBorrow = () => {
    if (canBorrow && availableCopies[0]) {
      setShowBorrowModal(true);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Back Button */}
      <TISAButton
        onClick={() => navigate(-1)}
        variant="outline"
        icon={<ArrowLeft className="h-4 w-4" />}
      >
        Back to Books
      </TISAButton>

      {/* Main Book Details */}
      <TISACard themeColor={book.theme_color} size="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Book Cover */}
          <div className="space-y-4">
            <div className="aspect-[3/4] overflow-hidden rounded-xl bg-muted shadow-lg">
              {book.cover_url ? (
                <img
                  src={book.cover_url}
                  alt={`Cover of ${book.title}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/20">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📚</div>
                    <div className="text-child-base text-muted-foreground font-medium">
                      {book.title}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Availability Status */}
            <TISACard variant="outline" size="sm">
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  <span className="font-semibold">Availability</span>
                </div>
                <div className="space-y-1">
                  <div
                    className={cn(
                      "text-child-sm font-medium",
                      availableCopies.length > 0
                        ? "text-green-600"
                        : "text-destructive",
                    )}
                  >
                    {availableCopies.length > 0
                      ? `${availableCopies.length} available`
                      : "All copies checked out"}
                  </div>
                  <div className="text-child-xs text-muted-foreground">
                    {totalCopies} total copies
                  </div>
                </div>
              </div>
            </TISACard>
          </div>

          {/* Book Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and Author */}
            <div className="space-y-2">
              <h1 className="text-child-3xl font-bold text-primary leading-tight">
                {book.title}
              </h1>
              <p className="text-child-xl text-muted-foreground">
                by {book.author}
              </p>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="text-child-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Theme
                </div>
                <div
                  className={cn(
                    `theme-${book.theme_color} px-3 py-2 rounded-lg text-center`,
                  )}
                >
                  <div className="text-xl">{theme.emoji}</div>
                  <div className="text-child-xs font-medium">{theme.name}</div>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-child-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Type
                </div>
                <div className="text-child-sm font-medium">
                  {book.type === "FIC" ? "Fiction" : "Non-Fiction"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-child-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Age Group
                </div>
                <div className="text-child-sm font-medium">
                  {book.age_group} years
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-child-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <MapPin className="h-3 w-3 inline mr-1" />
                  Location
                </div>
                <div className="text-child-sm font-medium">
                  {book.shelf_code}
                </div>
              </div>
            </div>

            {/* Tags */}
            {book.tags && book.tags.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  <span className="text-child-sm font-medium">Topics</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {book.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted rounded-full text-child-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Theme Description */}
            <TISACard variant="outline" size="sm">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="text-lg">{theme.emoji}</div>
                  <span className="font-semibold text-child-base">
                    About this theme
                  </span>
                </div>
                <p className="text-child-sm text-muted-foreground">
                  {theme.description}
                </p>
              </div>
            </TISACard>

            {/* Loan Status for Students */}
            {user?.role === "student" && loanStats && (
              <TISACard variant="outline" size="sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-tisa-yellow-600" />
                    <span className="font-semibold text-child-sm">
                      Your Library Status
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-child-xs">
                    <div>
                      <div className="text-muted-foreground">Current Loans</div>
                      <div className="font-semibold">
                        {loanStats.currentLoans} / {loanStats.maxLoans}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">TISAcoins</div>
                      <div className="font-semibold">{user.coins ?? 0} 🪙</div>
                    </div>
                  </div>
                  {!loanStats.canBorrowMore && (
                    <div className="text-child-xs text-orange-600 font-medium">
                      You've reached your borrowing limit. Return a book to
                      borrow more!
                    </div>
                  )}
                </div>
              </TISACard>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              {canBorrow ? (
                <TISAButton
                  onClick={handleBorrow}
                  variant="theme-green"
                  size="lg"
                  icon={<BookOpen className="h-5 w-5" />}
                  className="flex-1"
                >
                  Borrow This Book 📚
                </TISAButton>
              ) : user?.role === "student" && !loanStats?.canBorrowMore ? (
                <TISAButton
                  variant="outline"
                  size="lg"
                  disabled
                  icon={<Clock className="h-5 w-5" />}
                  className="flex-1"
                >
                  Borrowing Limit Reached
                </TISAButton>
              ) : availableCopies.length === 0 ? (
                <TISAButton
                  variant="outline"
                  size="lg"
                  disabled
                  icon={<Clock className="h-5 w-5" />}
                  className="flex-1"
                >
                  All Copies Checked Out
                </TISAButton>
              ) : user?.role !== "student" ? (
                <TISAButton
                  variant="outline"
                  size="lg"
                  disabled
                  className="flex-1"
                >
                  Student Login Required
                </TISAButton>
              ) : (
                <TISAButton
                  variant="outline"
                  size="lg"
                  disabled
                  className="flex-1"
                >
                  Cannot Borrow
                </TISAButton>
              )}

              <TISAButton
                variant="outline"
                size="lg"
                icon={<Star className="h-5 w-5" />}
              >
                Add to Wishlist
              </TISAButton>
            </div>
          </div>
        </div>
      </TISACard>

      {/* Copy Status Details */}
      {user?.role === "librarian" && (
        <TISACard>
          <div className="space-y-4">
            <h3 className="text-child-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Copy Management
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {book.copies?.map((copy) => (
                <div
                  key={copy.id}
                  className={cn(
                    "p-4 rounded-lg border-2",
                    copy.status === "available"
                      ? "border-green-200 bg-green-50"
                      : "border-orange-200 bg-orange-50",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Copy {copy.copy_code}</div>
                      <div className="text-child-sm text-muted-foreground">
                        Condition: {copy.condition}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "px-2 py-1 rounded text-child-xs font-medium",
                        copy.status === "available"
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800",
                      )}
                    >
                      {copy.status}
                    </div>
                  </div>
                  {copy.status === "borrowed" &&
                    copy.loans &&
                    copy.loans[0] && (
                      <div className="mt-2 text-child-xs text-muted-foreground">
                        Borrowed by: {copy.loans[0].user?.name} (
                        {copy.loans[0].user?.class})
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>
        </TISACard>
      )}

      {/* More Books from Same Theme */}
      <TISACard themeColor={book.theme_color}>
        <div className="space-y-4">
          <h3 className="text-child-lg font-bold flex items-center gap-2">
            <div className="text-xl">{theme.emoji}</div>
            More from "{theme.name}"
          </h3>
          <p className="text-child-sm text-muted-foreground">
            Explore more books about {theme.description.toLowerCase()}
          </p>
          <TISAButton
            onClick={() => navigate(`/student/books?theme=${book.theme_color}`)}
            variant="outline"
          >
            Browse {theme.name} Books
          </TISAButton>
        </div>
      </TISACard>

      {/* Borrow Modal */}
      <BorrowBookModal
        isOpen={showBorrowModal}
        onClose={() => setShowBorrowModal(false)}
        book={book}
        availableCopyId={availableCopies[0]?.id || ""}
      />
    </div>
  );
}
